import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
  FormField,
  Input,
} from '@yeshuman/sdk'
import { useMemo, useState } from 'react'

import { useScheduleInterview } from '../../hooks/use-interviewer-candidates'

const DEFAULT_MINUTES = 45

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function defaultStart(): Date {
  const d = new Date()
  d.setMinutes(0, 0, 0)
  d.setHours(d.getHours() + 1)
  return d
}

export function ScheduleInterviewCanvas({
  applicationId,
  candidateName,
  onClose,
}: {
  applicationId: string
  candidateName: string
  onClose: () => void
}) {
  const schedule = useScheduleInterview(applicationId)
  const initialStart = useMemo(() => defaultStart(), [])
  const [startLocal, setStartLocal] = useState(() => toLocalInputValue(initialStart))
  const [roundName, setRoundName] = useState('Interview')
  const [error, setError] = useState<string | null>(null)

  const onSave = () => {
    const start = new Date(startLocal)
    if (Number.isNaN(start.getTime())) {
      setError('Choose a start time.')
      return
    }
    const end = new Date(start.getTime() + DEFAULT_MINUTES * 60_000)
    setError(null)
    schedule.mutate(
      {
        round_name: roundName.trim() || 'Interview',
        scheduled_start: start.toISOString(),
        scheduled_end: end.toISOString(),
      },
      {
        onSuccess: () => onClose(),
        onError: () => setError('Could not schedule the interview.'),
      },
    )
  }

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <p className="text-sm text-muted-foreground">
          Book {candidateName} for a {DEFAULT_MINUTES}-minute virtual interview. You can start the
          video room from Home or Candidates when you are ready.
        </p>
        <FormField label="Round" htmlFor="iv-round">
          <Input
            id="iv-round"
            value={roundName}
            onChange={(e) => setRoundName(e.target.value)}
          />
        </FormField>
        <FormField label="Starts" htmlFor="iv-start">
          <Input
            id="iv-start"
            type="datetime-local"
            value={startLocal}
            onChange={(e) => setStartLocal(e.target.value)}
          />
        </FormField>
      </div>
      <ArtifactCanvasInlineError>{error}</ArtifactCanvasInlineError>
      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={onSave}
            isDirty
            isSaving={schedule.isPending}
            saveLabel="Schedule"
            savingLabel="Scheduling…"
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
